import React, { useEffect, useRef, useState } from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import {
  ROOF_COVER_OPTIONS,
  parseRoofCoverValues,
  roofCoverCarryOver,
  roofCoverOptionState,
  toggleRoofCoverOption,
} from "../../../../helpers/roofCover";

const optionId = (idPrefix, option) =>
  `${idPrefix}_${option.label.toLowerCase().replace(/[^a-z0-9]+/g, "_")}`;

const includesText = (option) =>
  `${option.formerly ? `Formerly ${option.formerly}. ` : ""}Includes roofs recorded as: ${option.values.join(", ")}.`;

function OptionCheckbox({ id, name, option, state, onToggle }) {
  const ref = useRef(null);
  // Indeterminate is a DOM property with no attribute, so React can't set it.
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === "partial";
  }, [state]);

  return (
    <input
      ref={ref}
      type="checkbox"
      id={id}
      name={name}
      value={option.values.join(",")}
      checked={state === "checked"}
      onChange={() => onToggle(option)}
    />
  );
}

/**
 * The info button sits beside the label, never inside it: a click inside a
 * <label> toggles its checkbox, and reading what Shingle covers must not
 * change the search. The same text is in a hidden description, so a screen
 * reader gets it without opening the tooltip.
 */
function OptionInfo({ id, option }) {
  const tooltipId = `${id}_info`;
  const descriptionId = `${id}_includes`;

  return (
    <>
      <button
        type="button"
        className="roof_cover_info"
        aria-label={`What ${option.label} includes`}
        aria-describedby={descriptionId}
        data-tooltip-id={tooltipId}
      >
        <img src="/images/info.svg" alt="" />
      </button>
      <span id={descriptionId} hidden>
        {includesText(option)}
      </span>
      <ReactTooltip
        id={tooltipId}
        className="roof_cover_tooltip"
        place="top"
        positionStrategy="fixed"
        opacity={1}
        border="1px solid #d7c1a2"
        openEvents={{ mouseenter: true, focus: true, click: true }}
        closeEvents={{ mouseleave: true, blur: true }}
        globalCloseEvents={{ escape: true, clickOutsideAnchor: true, scroll: true }}
      >
        {option.formerly && <p>Formerly “{option.formerly}”.</p>}
        <p>Includes roofs recorded as:</p>
        <ul>
          {option.values.map((value) => (
            <li key={value}>{value}</li>
          ))}
        </ul>
      </ReactTooltip>
    </>
  );
}

/**
 * One row per Roof Cover option: checkbox, label and info tooltip. Both
 * filter forms render these rows, so the options, what each one sends and
 * what its tooltip says come from ROOF_COVER_OPTIONS alone.
 */
export function RoofCoverOptions({ idPrefix, name, selectedValues, onToggle }) {
  return ROOF_COVER_OPTIONS.map((option) => {
    const id = optionId(idPrefix, option);
    return (
      <div className="lbl_btn roof_cover_option" key={option.label}>
        <OptionCheckbox
          id={id}
          name={name}
          option={option}
          state={roofCoverOptionState(option, selectedValues)}
          onToggle={onToggle}
        />
        <label htmlFor={id}>{option.label}</label>
        <OptionInfo id={id} option={option} />
      </div>
    );
  });
}

/**
 * Shown only for a search saved while the materials were separate
 * checkboxes. It still filters exactly as saved, which no ticked box shows.
 */
export function RoofCoverCarryOverNote({ selectedValues }) {
  const { partial, unknown } = roofCoverCarryOver(selectedValues);
  if (partial.length === 0 && unknown.length === 0) return null;

  const applied = [
    ...partial.map(({ label, values }) => `${label} (${values.join(", ")} only)`),
    ...unknown,
  ];
  return (
    <p className="roof_cover_note" role="note">
      This search still filters on {applied.join("; ")} from before Roof Cover
      options were combined. Tick an option to include every type it covers.
    </p>
  );
}

/**
 * The collapsible Roof Cover box: title, "(N selected)", the option grid and
 * the older-search note. The search page drives it through react-hook-form
 * (RoofCoverField below); the older form in Filters-search.jsx passes its own
 * selection and toggle handler.
 *
 * Collapsing hides the grid instead of unmounting it: the older form reads
 * ticked boxes straight off the DOM when it submits, so unmounted boxes
 * would silently drop the filter.
 */
export function RoofCoverGroup({ idPrefix, name, selectedValues, onToggle, columns = 4 }) {
  const [open, setOpen] = useState(true);
  const selectedCount =
    ROOF_COVER_OPTIONS.filter(
      (option) => roofCoverOptionState(option, selectedValues) !== "unchecked"
    ).length + roofCoverCarryOver(selectedValues).unknown.length;

  return (
    <div className="form_blk multi_check_group">
      <div
        className="info_lbl_lbl multi_check_toggle"
        style={{ cursor: "pointer", userSelect: "none" }}
        onClick={() => setOpen((prev) => !prev)}
      >
        <h6>
          Roof Cover
          {selectedCount > 0 && (
            <span className="selected_badge" style={{ marginLeft: 6, fontSize: 12, color: "#67773F" }}>
              ({selectedCount} selected)
            </span>
          )}
        </h6>
        <span style={{ fontSize: 12 }}>{open ? "▲" : "▼"}</span>
      </div>

      <div hidden={!open}>
        <div
          className="multi_check_list roof_cover_list"
          style={{
            border: "1px solid #ddd",
            borderRadius: 4,
            padding: "10px",
            marginTop: 4,
            display: "grid",
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            rowGap: "6px",
            columnGap: "10px",
          }}
        >
          <RoofCoverOptions
            idPrefix={idPrefix}
            name={name}
            selectedValues={selectedValues}
            onToggle={onToggle}
          />
        </div>
        <RoofCoverCarryOverNote selectedValues={selectedValues} />
      </div>
    </div>
  );
}

export default function RoofCoverField({ filterData, setValue, columns = 4 }) {
  const selectedValues = parseRoofCoverValues(filterData?.building_roofCover);

  return (
    <RoofCoverGroup
      idPrefix="building_roofCover"
      name="building_roofCover"
      selectedValues={selectedValues}
      columns={columns}
      onToggle={(option) =>
        setValue("building_roofCover", toggleRoofCoverOption(selectedValues, option), {
          shouldDirty: true,
        })
      }
    />
  );
}
